import { inject, Injectable, NgZone } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private timeoutId: ReturnType<typeof setTimeout> | undefined;
  private readonly idleMs = 60 * 60 * 1000; // 60 minutes default
  private readonly refreshAheadMs = 5 * 60 * 1000;
  private readonly refreshThrottleMs = 60 * 1000;
  private lastRefreshCheckAt = 0;

  private zone = inject(NgZone);
  private msal = inject(MsalService);

  constructor() {
    this.bind();
    this.reset();
  }

  private bind(): void {
    ['mousemove', 'keydown', 'touchstart', 'scroll', 'click'].forEach((evt) =>
      globalThis.addEventListener(
        evt,
        () => {
          this.reset();
          this.refreshTokenOnActivity();
        },
        { passive: true }
      )
    );
  }

  private reset(): void {
    this.zone.runOutsideAngular(() => {
      clearTimeout(this.timeoutId);
      this.timeoutId = setTimeout(() => this.onIdle(), this.idleMs);
    });
  }

  private onIdle(): void {
    // If we are in an auth error state, don't trigger login redirects
    try {
      if (sessionStorage.getItem('auth_error') === '1') {
        return;
      }
    } catch {
      /* ignore storage errors */
    }
    // Trigger re-authentication by redirecting to login
    this.msal.loginRedirect();
  }

  private refreshTokenOnActivity(): void {
    const now = Date.now();
    if (now - this.lastRefreshCheckAt < this.refreshThrottleMs) {
      return;
    }
    this.lastRefreshCheckAt = now;
    if (!this.isTokenNearExpiration()) {
      return;
    }
    const scopes = environment.api.scopes.length > 0 ? environment.api.scopes : environment.basicScopes;
    this.msal.acquireTokenSilent({ scopes, forceRefresh: false }).subscribe({
      next: (result) => {
        if (result.idToken) {
          sessionStorage.setItem('idToken', result.idToken);
        }
        if (result.accessToken) {
          sessionStorage.setItem('accessToken', result.accessToken);
        }
      },
    });
  }

  private isTokenNearExpiration(): boolean {
    const token = sessionStorage.getItem('idToken');
    if (!token) {
      return false;
    }
    const payloadPart = token.split('.')[1];
    if (!payloadPart) {
      return false;
    }
    try {
      const payloadJson = atob(payloadPart.replaceAll('-', '+').replaceAll('_', '/'));
      const payload = JSON.parse(payloadJson) as { exp?: number };
      if (!payload.exp || typeof payload.exp !== 'number') {
        return false;
      }
      const expiresAt = payload.exp * 1000;
      return expiresAt - Date.now() <= this.refreshAheadMs;
    } catch {
      return false;
    }
  }
}
